import { COMPLIANCE_STATUS, TAG_DATA_TYPES, TRACEABILITY_LEVEL } from "../../@core/models/enums";
import { Document, IAttachment, IDocumentKnowledge, IOrganization, IUser } from "../../@core/models/model";
import { IContract, IFormAnswer, IFormQuestion } from "./contract-management.model";


export interface ITag extends Document {
    organization: string | IOrganization;
    name: any;
    tagType: string | ITagType;
    type: string | TAG_TYPES;
    createdAt: Date;
    updatedAt: Date;
    createdBy: string | IUser;
}

export interface IClient extends ITag {
    address: string,
    licenseNumber: string,
    fax: string,
    scope: string,
    website: string,
}

export interface ILocation extends ITag {
    address: string,
    city: string,
    state: string,
    zip: string,
    country: string,
    latitude: number,
    longitude: number,
}

export interface ITypeOfContract extends ITag {
    description: string
}

export interface IScopeOfWork extends ITag {
    description: string
}

export interface ICustom extends ITag {
    name: string
}

export interface IRepoExtracted extends ITag {
    targetTagType?: string | TAG_TYPES;
    reason?: string
}

export interface IProject extends ITag {
    id: string,
    name: string,
    description: string
}

export enum mappingType {
    ALL = 'ALL',
    CUSTOM = 'CUSTOM'
}

export interface IChecklistItem extends Document {
    organization: string | IOrganization,
    documentType: string | IDocumentType,
    name: string,
    description: string,
    mapping: mappingType,
}

export enum TAG_TYPES {
    CLIENT = 'CLIENT',
    LOCATION = 'LOCATION',
    TYPE_OF_CONTRACT = 'TYPE_OF_CONTRACT',
    SCOPE_OF_WORK = 'SCOPE_OF_WORK',
    PROJECT = 'PROJECT',
    CUSTOM = 'CUSTOM',
    REPO_EXTRACTED = 'REPO_EXTRACTED',
}

export enum TAG_FEATURE {
    PROJECT = 'Project',
    DOCUMENT = 'Document',
}

export interface ITagType {
    readonly _id?: string,
    name: string,
    description?: string,
    tagType: TAG_TYPES,
    color?: string,
    dataType: TAG_DATA_TYPES,
    mandatory?: boolean,
    feature?: TAG_FEATURE | TAG_FEATURE[],
    documentType?: string | IDocumentType,
    options?: string[]
}

export interface IStandardDocumentType extends Document {
    name: string,
    description: string,
}

export enum KPI_TYPE {
    STANDARD = 'STANDARD',
    CUSTOM = 'CUSTOM',
    LINKED = 'LINKED'
}

export enum KPI_DATA_TYPE {
    'TEXT' = 'TEXT',
    'TABLE' = 'TABLE',
    'GRAPH' = 'GRAPH',
}

export interface IKpi {
    _id?: string,
    name: string,
    description: string,
    kpi_type?: KPI_TYPE,
    docType?: string | IStandardDocumentType
    data_type?: KPI_DATA_TYPE
    linkedTo?: string
}

export interface ICompareMatrix {
    entity: string,
    status: COMPLIANCE_STATUS,
    score: number | string,
    confidence: number | string,
    traceability: TRACEABILITY_LEVEL,
    summary: string,
}

export interface ICompareKpi {
    kpi: string,
    kpiDoc: IKpi,
    expanded: boolean,
    kpiData: {
        conclusions?: { message: string, status: COMPARE_DOCUMENT_STATUS }[],
        last_conclusion?: { message: string, status: COMPARE_DOCUMENT_STATUS },
        full_plan?: { title: string, description: string, expected_output: string }[],
        final_response?: string,
        report?: string,
        matrix?: ICompareMatrix[],
        sources?: any,
        token_data?: any,
        total_time?: number
    },
    kpiStatus: COMPARE_DOCUMENT_STATUS,
    /** Why the KPI was paused; 'credits_exhausted' triggers the credits-overview UI. */
    pauseReason?: string | null,
}
export enum COMPARE_DOCUMENT_STATUS {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
    NO_INSIGHTS = 'NO_INSIGHTS',
    /** KPI run was paused — user paused, server restart, or credits exhausted. */
    PAUSED = 'PAUSED',
}

export interface IDocumentType extends Document {
    organization: string | IOrganization,
    name: string,
    description: string,
    summaryDescription?: string,
    comparisionDescription?: string,
    standardDocumentType: string | IStandardDocumentType | null,
    linkedTagTypes: string[] | ITagType[],
    createdAt?: Date,
    customKpis?: IKpi[],
    containsForm?: boolean
}

export enum KEY_POINT_MODELS {
    CLIENT = 'VendeeSubContractor',
    CONTRACT_CATEGORY = 'ContractCategory'
}
export interface IChecklistItemTag extends Document {
    organization: string | IOrganization,
    tagType: string | ITagType,
    checklist: string | IChecklistItem,
    value: string | ITag,
}

export interface IChecklistItemRule extends Document {
    organization: string | IOrganization,
    checklist: string | IChecklistItem,
    impact: number
    impactRules: {
        rule: string,
        impact: number
    }[],
    probability: number
}

export interface IConstructionAttachment extends Document {
    organization: string | IOrganization;
    name: string;
    description: string;
    attachment: IAttachment | null;
    expiryDate: string | Date | null;
    createdBy: string | IUser;
    active: boolean;
    createdAt: Date;
    updatedAt: Date;
    documentKnowledge?: IDocumentKnowledge;
}

export enum QNA_PAIR_METADATA_STATUS {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
}

export interface IQnaPairMetadata extends Document {
    organization: string | IOrganization;
    documentType: string | IDocumentType;
    upload: {
        attachment?: IAttachment; //uploaded file
        status: QNA_PAIR_METADATA_STATUS;
    }
    createdBy: string | IUser;
    createdAt: Date;
    updatedAt: Date;
}

export interface IQnaPair extends Document {
    organization: string | IOrganization;
    documentType: string | IDocumentType;

    question: string | IFormQuestion;
    answer: string | IFormAnswer;

    timesUsed: number;

    author: string | IUser; // created By

    document?: string | IContract; // in case it was added through a document/contract

    metadata?: string | IQnaPairMetadata;
    createdAt: Date;
    updatedAt: Date;
}
